import { deepMerge } from "@/utilities/functions/deepMerge";
import { flatToNestedMessages } from "@/utilities/functions/flatToNestedMessages";

export interface LocalizedSource {
  /** Nome leggibile della sorgente, usato nei log quando fallisce (es. "language/localized") */
  name: string;
  result: PromiseSettledResult<Record<string, unknown>>;
}

export interface FailedLocalizedSource {
  name: string;
  reason: unknown;
}

export interface CollectedLocalizedSources {
  messages: Record<string, unknown>;
  failed: FailedLocalizedSource[];
}

function isMessagesRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Funzione pura: unisce i messaggi i18n delle sorgenti che hanno risposto e
 * raccoglie quelle fallite. NON decide se il degrado è accettabile: quella
 * scelta è del chiamante (vedi store/locale.ts).
 *
 * Le sorgenti sono applicate in ordine: l'ultima vince sulle chiavi in conflitto.
 */
export function collectLocalizedSources(sources: LocalizedSource[]): CollectedLocalizedSources {
  if (sources.length === 0) {
    throw new Error("collectLocalizedSources: sources must not be empty");
  }
  if (sources.some((source) => !source.name.trim())) {
    throw new Error("collectLocalizedSources: every source must have a name");
  }

  let messages: Record<string, unknown> = {};
  const failed: FailedLocalizedSource[] = [];

  for (const source of sources) {
    if (source.result.status === "rejected") {
      failed.push({ name: source.name, reason: source.result.reason });
      continue;
    }

    // Un 200 con corpo non utilizzabile (null, array, stringa) vale come fallimento:
    // meglio perdere una sorgente che far esplodere l'espansione delle chiavi.
    if (!isMessagesRecord(source.result.value)) {
      failed.push({
        name: source.name,
        reason: new Error(`${source.name}: risposta priva di messaggi`),
      });
      continue;
    }

    messages = deepMerge(messages, flatToNestedMessages(source.result.value));
  }

  return { messages, failed };
}
